import type { FC, HTMLAttributes, ReactNode } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { apiUrl } from '../../config';
import { usePermissions } from '../../hooks/usePermissions';
import { commonMessages } from '../../i18n/common';
import axios from '../../services/axios';
import { GraphModal } from '../GraphModal/GraphModal';
import type { ModalInstance } from '../GraphModal/Modal';

const messages = defineMessages({
  confirmDeleteAccountTitle: {
    defaultMessage: 'Are you sure you want to delete this account?',
    id: 'tnGhkz',
  },
  confirmDeleteAccountBody: {
    defaultMessage: `<p>Warning! Deleting this account will also delete:</p>
<p>- all linked sites</p>
<p>- all profiles created on these sites</p>
<sitesList>{sites}</sitesList>
<p>- all comments posted on these sites</p>`,
    id: 'bTFouW',
  },
});

type AccountDeletionModaleProps = HTMLAttributes<HTMLDivElement> & {
  poper: ReactNode;
  onShowImmediate?: () => void | Promise<void>;
};

export const AccountDeletionModale: FC<AccountDeletionModaleProps> = memo(
  function AccountDeletionModale({ poper, className, onShowImmediate, ...otherProps }) {
    const [sites, setSites] = useState<string[]>([]);
    const { logout } = usePermissions();

    useEffect(() => {
      let mounted = true;

      axios
        .get<{ status?: string; website?: { name?: string } }[]>(`${apiUrl}/api/users/profiles`)
        .then(resp => {
          if (!mounted) return;
          const names =
            resp.data
              ?.filter(profile => profile.status === 'validated')
              .map(profile => profile.website?.name)
              .filter((name): name is string => !!name) ?? [];
          setSites(names);
        })
        .catch(() => {
          // if error, leave the list empty
          console.warn('Error fetching sites: keep empty list');
        });

      return () => {
        mounted = false;
      };
    }, []);

    const handleConfirmDeleteAccount = useCallback(async () => {
      try {
        await axios.delete(`${apiUrl}/api/users/deleteUser`);
        await logout();
        toast.success(<FormattedMessage {...commonMessages.deletedSuccessfully} />);
      } catch (error) {
        console.error('Error deleting account:', error);
      }
    }, [logout]);

    const lazyRender = useCallback(
      (modal: ModalInstance) => (
        <div className="modal-title text-sm">
          <h2 className="mb-4 text-lg font-semibold text-[#f44336]">
            <FormattedMessage {...messages.confirmDeleteAccountTitle} />
          </h2>
          <div className="modal-body mb-6">
            <FormattedMessage
              {...messages.confirmDeleteAccountBody}
              values={{
                p: (chunks: React.ReactNode) => <p className="mb-2">{chunks}</p>,
                sitesList: (chunks: React.ReactNode) => (
                  <div className="mb-2 ml-6 text-gray-600 text-xs">{chunks}</div>
                ),
                sites: sites?.join(', ') || '',
              }}
              tagName={'div'}
            />
            <FormattedMessage {...commonMessages.areYouSure} />
          </div>

          <div className="flex justify-end gap-6">
            <button type="button" className="text-sm text-gray-500" onClick={modal.hide}>
              <FormattedMessage {...commonMessages.no} />
            </button>
            <button
              type="button"
              className="px-4 py-1 text-sm text-white bg-primary rounded"
              onClick={async () => {
                await handleConfirmDeleteAccount();
                modal.hide();
              }}
            >
              <FormattedMessage {...commonMessages.yes} />
            </button>
          </div>
        </div>
      ),
      [sites, handleConfirmDeleteAccount],
    );

    return (
      <div className={twMerge('AccountDeletionModale', className)} {...otherProps}>
        <GraphModal
          lazyContent={lazyRender}
          onShowImmediate={onShowImmediate}
          showPageOverlay
          forceModal
        >
          {poper}
        </GraphModal>
      </div>
    );
  },
);
